import {
  type CardDispute,
  CardDisputeCloseResolution,
  type CardDisputeDocumentEvidenceInput,
  type CardDisputeEvent,
  type CardDisputeEvidence,
  CardDisputeResponseAction,
  type RespondCardDisputeRequest,
} from '@column/api';

import { parseMajorUnitsToSmallestUnit } from '../Initiation/questionnaire';

/**
 * Part 05 — response, retry, and closure decision matrix.
 *
 * Every control here is gated on the server-derived `allowed_response_actions` /
 * `allowed_close_resolutions` allowlists. Stage and workflow strings only select copy; they never
 * enable an action the server did not offer. The backend revalidates under lock.
 */

export interface DisputeResponseActionDescriptor {
  action: CardDisputeResponseAction;
  label: string;
  description: string;
  requiresNarrative: boolean;
  requiresAmount: boolean;
  confirmHeadline: string;
  confirmDescription: string;
}

export type ResponseActionCopy = Omit<
  DisputeResponseActionDescriptor,
  'action' | 'requiresNarrative' | 'requiresAmount'
>;

const ACCEPT_MERCHANT_RESPONSE: ResponseActionCopy = {
  label: 'Accept merchant response',
  description:
    'Accepting the merchant response ends the dispute in the merchant’s favor. This is not the same as accepting the cardholder’s claim — review the merchant’s evidence, explain why you are accepting it, and note that any outstanding provisional credit is reversed.',
  confirmHeadline: 'Accept the merchant’s response?',
  confirmDescription:
    'The dispute closes in the merchant’s favor, any outstanding provisional credit is reversed, and no further response can be sent. This does not rule on the cardholder’s claim.',
};

const ACCEPT_ACQUIRER_ANSWER: ResponseActionCopy = {
  label: 'Accept the acquirer’s answer',
  description:
    'The acquirer may have accepted in full, accepted in part, or declined. Accepting takes their answer as-is, whatever it was, and reverses any outstanding provisional credit — review what they actually said before deciding.',
  confirmHeadline: 'Accept the acquirer’s answer?',
  confirmDescription:
    'The acquirer’s pre-arbitration answer stands as given and the dispute closes; any outstanding provisional credit is reversed.',
};

const ACCEPT_ALLOCATION_CLAIM: ResponseActionCopy = {
  label: 'Accept in full',
  description:
    'Accept the acquirer’s fraud claim. The platform takes liability for the full dispute amount and any outstanding provisional credit is reversed.',
  confirmHeadline: 'Accept the acquirer’s claim in full?',
  confirmDescription:
    'The platform accepts liability for the full dispute amount and any outstanding provisional credit is reversed. This cannot be undone.',
};

const ACCEPT_PARTIAL_COPY: ResponseActionCopy = {
  label: 'Accept partially',
  description:
    'Accept part of the acquirer’s claim. Enter the portion of liability the platform accepts; the rest is declined with your explanation. Any outstanding provisional credit is reversed.',
  confirmHeadline: 'Accept the acquirer’s claim partially?',
  confirmDescription:
    'The platform accepts liability for the amount entered. Under the current Card Network flow this ends the dispute, reverses any outstanding provisional credit, and files no separate remainder dispute.',
};

const DECLINE_COPY: ResponseActionCopy = {
  label: 'Decline claim',
  description:
    'Contest the acquirer’s claim. Explain why and attach any supporting evidence — the Card Network weighs the issuer’s response.',
  confirmHeadline: 'Decline the acquirer’s claim?',
  confirmDescription:
    'Your explanation and evidence are sent to the Card Network and the acquirer decides the next step.',
};

const ESCALATE_PREARB_COPY: ResponseActionCopy = {
  label: 'Escalate to pre-arbitration',
  description:
    'Continue to the Card Network pre-arbitration instead of accepting the merchant response. Explain why and attach any supporting evidence.',
  confirmHeadline: 'Escalate to pre-arbitration?',
  confirmDescription:
    'The issuer starts pre-arbitration with the Card Network. Your explanation and evidence are sent with it.',
};

const REQUEST_ARBITRATION_COPY: ResponseActionCopy = {
  label: 'Request arbitration',
  description:
    'Ask Column’s disputes team to consider filing arbitration with the Card Network. This requests an operations handoff — Column files with the Card Network, not this dashboard.',
  confirmHeadline: 'Request arbitration filing?',
  confirmDescription:
    'Column’s disputes team reviews the request and files with the Card Network when appropriate. Filing and the ruling are tracked on this page.',
};

/** Copy keyed by workflow and stage; missing entries mean the action is not rendered (fail closed). */
const RESPONSE_ACTION_CONTEXT_COPY: Partial<
  Record<CardDisputeResponseAction, Partial<Record<string, ResponseActionCopy>>>
> = {
  [CardDisputeResponseAction.ACCEPT]: {
    'collaboration:merchant_responded': ACCEPT_MERCHANT_RESPONSE,
    'collaboration:prearbitration_responded': ACCEPT_ACQUIRER_ANSWER,
    'allocation:prearbitration': ACCEPT_ALLOCATION_CLAIM,
  },
  [CardDisputeResponseAction.ACCEPT_PARTIAL]: {
    'allocation:prearbitration': ACCEPT_PARTIAL_COPY,
  },
  [CardDisputeResponseAction.DECLINE]: {
    'allocation:prearbitration': DECLINE_COPY,
  },
  [CardDisputeResponseAction.ESCALATE]: {
    'collaboration:merchant_responded': ESCALATE_PREARB_COPY,
    'collaboration:prearbitration_responded': REQUEST_ARBITRATION_COPY,
  },
};

const RESPONSE_ACTION_ORDER: CardDisputeResponseAction[] = [
  CardDisputeResponseAction.ACCEPT,
  CardDisputeResponseAction.ACCEPT_PARTIAL,
  CardDisputeResponseAction.DECLINE,
  CardDisputeResponseAction.ESCALATE,
];

const contextKeyOf = (dispute: CardDispute): string => `${dispute.workflow}:${dispute.stage}`;

const responseActionCopy = (dispute: CardDispute, action: CardDisputeResponseAction): ResponseActionCopy | undefined =>
  RESPONSE_ACTION_CONTEXT_COPY[action]?.[contextKeyOf(dispute)];

const responseActionInputs = (
  action: CardDisputeResponseAction
): Pick<DisputeResponseActionDescriptor, 'requiresNarrative' | 'requiresAmount'> => ({
  requiresNarrative: action !== CardDisputeResponseAction.ACCEPT,
  requiresAmount: action === CardDisputeResponseAction.ACCEPT_PARTIAL,
});

/**
 * The response actions the server currently accepts, in a stable display order. Unknown allowlist
 * values are dropped rather than guessed at.
 */
export const getResponseActionDescriptors = (dispute: CardDispute): DisputeResponseActionDescriptor[] =>
  RESPONSE_ACTION_ORDER.filter((action) => dispute.allowed_response_actions?.includes(action))
    .map((action) => {
      const copy = responseActionCopy(dispute, action);
      return copy ? { action, ...responseActionInputs(action), ...copy } : undefined;
    })
    .filter((descriptor): descriptor is DisputeResponseActionDescriptor => descriptor !== undefined);

export interface DisputeCloseResolutionDescriptor {
  resolution: CardDisputeCloseResolution;
  label: string;
  description: string;
  confirmHeadline: string;
  confirmDescription: string;
}

const CLOSE_RESOLUTION_COPY: Record<
  CardDisputeCloseResolution,
  Omit<DisputeCloseResolutionDescriptor, 'resolution'>
> = {
  [CardDisputeCloseResolution.ACCEPT_CARDHOLDER_CLAIM]: {
    label: 'Accept cardholder claim',
    description:
      'The platform accepts liability: the cardholder receives or keeps credit through the existing backend flow. The program operating account will fund the credit where applicable.',
    confirmHeadline: 'Accept the cardholder’s claim?',
    confirmDescription:
      'The platform accepts liability and the cardholder keeps or receives credit, funded by the program operating account. This cannot be undone.',
  },
  [CardDisputeCloseResolution.CARDHOLDER_WITHDREW]: {
    label: 'Cardholder withdrew claim',
    description:
      'The cardholder withdrew their claim and the dispute is closed. Any provisional credit will be reversed.',
    confirmHeadline: 'Close as cardholder withdrawal?',
    confirmDescription:
      'The dispute is closed as withdrawn. Where provisional credit was issued, its reversal follows the backend rules and may be recalled from the network for submitted disputes.',
  },
};

/** The close resolutions the server currently accepts, in the enum's declared order. */
export const getCloseResolutionDescriptors = (dispute: CardDispute): DisputeCloseResolutionDescriptor[] =>
  (Object.values(CardDisputeCloseResolution) as CardDisputeCloseResolution[])
    .filter((resolution) => dispute.allowed_close_resolutions?.includes(resolution))
    .map((resolution) => ({ resolution, ...CLOSE_RESOLUTION_COPY[resolution] }));

export const getCloseResolutionLabel = (resolution: string): string =>
  (Object.values(CardDisputeCloseResolution) as CardDisputeCloseResolution[]).includes(
    resolution as CardDisputeCloseResolution
  )
    ? CLOSE_RESOLUTION_COPY[resolution as CardDisputeCloseResolution].label
    : resolution;

/** Who acts next, derived only from server-visible state. */
export const getNextActor = (dispute: CardDispute): string => {
  if (dispute.stage === 'dispute_won' || dispute.stage === 'dispute_close') {
    return 'No further action — this dispute is closed';
  }
  if (isClosurePending(dispute)) {
    return 'Column is processing the closure';
  }
  if (dispute.submission_status === 'pending_submission') {
    return 'Column is submitting to the Card Network';
  }
  if (dispute.submission_status === 'draft') {
    return 'You complete and submit the draft';
  }
  if (dispute.submission_status === 'failed') {
    return 'You retry the failed submission';
  }
  if (dispute.stage === 'arbitration') {
    return 'Column’s disputes team files with the Card Network; the Card Network rules';
  }
  if (dispute.workflow === 'allocation' && dispute.stage === 'prearbitration_responded') {
    return 'The acquirer decides whether to file arbitration';
  }
  if (dispute.allowed_response_actions && dispute.allowed_response_actions.length > 0) {
    return 'You choose the response';
  }
  return 'Awaiting network activity';
};

/** Inbound evidence, newest first — the material to review before responding. */
export const latestInboundEvidence = (dispute: CardDispute): CardDisputeEvidence[] =>
  [...(dispute.evidence ?? [])]
    .filter((entry) => entry.direction === 'inbound')
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));

/** A queued close keeps `requested_resolution` until the server reaches the terminal state. */
export const isClosurePending = (dispute: CardDispute): boolean =>
  dispute.submission_status === 'pending_submission' &&
  dispute.requested_resolution !== undefined &&
  dispute.requested_resolution !== null;

export type CardDisputeSubmissionIntent =
  | { kind: 'initial_submission' }
  | { kind: 'response'; action?: string }
  | { kind: 'close'; resolution?: string }
  | { kind: 'unknown' };

/**
 * The persisted intent the backend would replay from `/submit`, derived from Part 01's additive
 * fields (`requested_resolution`, event `response_action`) and the dispute shape. Unknown means
 * the intent cannot be determined safely and must not be retried from the browser.
 */
export const getSubmissionIntent = (dispute: CardDispute): CardDisputeSubmissionIntent => {
  if (dispute.requested_resolution) {
    return { kind: 'close', resolution: dispute.requested_resolution };
  }
  const lastResponseEvent = lastResponseIntentEvent(dispute.events ?? []);
  if (lastResponseEvent?.response_action) {
    return { kind: 'response', action: lastResponseEvent.response_action };
  }
  if (dispute.stage === 'initiated') {
    return { kind: 'initial_submission' };
  }
  return { kind: 'unknown' };
};

/** The last event carrying a stored response intent, or undefined. Internal `recall` values surface as history only. */
export const lastResponseIntentEvent = (events: CardDisputeEvent[]): CardDisputeEvent | undefined =>
  [...events]
    .filter((event) => typeof event.response_action === 'string' && (event.response_action as string) !== '')
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))[0];

/**
 * `/submit` replays the persisted intent: the initial filing, a saved response, or a saved
 * recall/close. Verified by the backend's dispute-sync tests; anything else must not retry.
 */
export const canRetrySubmissionIntent = (dispute: CardDispute): boolean => {
  const intent = getSubmissionIntent(dispute);
  return intent.kind !== 'unknown';
};

export const submissionIntentLabel = (intent: CardDisputeSubmissionIntent): string => {
  switch (intent.kind) {
    case 'initial_submission':
      return 'Initial submission to the Card Network';
    case 'response':
      return intent.action ? `Response (${intent.action})` : 'Response';
    case 'close':
      return intent.resolution
        ? `Closure / network recall (${getCloseResolutionLabel(intent.resolution)})`
        : 'Closure / network recall';
    default:
      return 'Unknown';
  }
};

export interface RespondFormInput {
  narrative: string;
  amountInput: string;
  evidence: CardDisputeDocumentEvidenceInput[];
}

const NARRATIVE_REQUIRED_ACTIONS = [
  CardDisputeResponseAction.ACCEPT_PARTIAL,
  CardDisputeResponseAction.DECLINE,
  CardDisputeResponseAction.ESCALATE,
];

/**
 * Client-side mirror of the backend's response validation: narrative required for
 * accept_partial/decline/escalate, amount only for accept_partial with 0 < amount < dispute
 * amount. The server revalidates under lock.
 */
export const validateRespondFormInput = (
  dispute: CardDispute,
  action: CardDisputeResponseAction,
  input: RespondFormInput
): string[] => {
  const problems: string[] = [];
  if (NARRATIVE_REQUIRED_ACTIONS.includes(action) && input.narrative.trim() === '') {
    problems.push('A written explanation is required.');
  }
  if (action === CardDisputeResponseAction.ACCEPT_PARTIAL) {
    const amount = parseMajorUnitsToSmallestUnit(input.amountInput, dispute.currency);
    if (input.amountInput.trim() === '' || amount === undefined) {
      problems.push(`Enter the accepted liability amount in ${dispute.currency}.`);
    } else if (amount <= 0) {
      problems.push('The accepted liability amount must be greater than zero.');
    } else if (amount >= dispute.amount) {
      problems.push(
        'The accepted liability amount must be less than the dispute amount. To accept everything, use Accept in full.'
      );
    }
  }
  if (action !== CardDisputeResponseAction.ACCEPT_PARTIAL && input.amountInput.trim() !== '') {
    problems.push('An amount can only be entered for a partial acceptance.');
  }
  if (
    action === CardDisputeResponseAction.ACCEPT_PARTIAL &&
    input.evidence.length > 0 &&
    input.evidence.some((entry) => !entry.evidence_type)
  ) {
    problems.push('Select an evidence category for each document.');
  }
  return problems;
};

/** The exact generated mutation payload for `/respond`. `recall` is never a legal input. */
export const buildRespondRequest = (
  dispute: CardDispute,
  action: CardDisputeResponseAction,
  input: RespondFormInput
): RespondCardDisputeRequest => {
  const request: RespondCardDisputeRequest = { action };
  const narrative = input.narrative.trim();
  if (narrative !== '') {
    request.narrative = narrative;
  }
  if (action === CardDisputeResponseAction.ACCEPT_PARTIAL) {
    const amount = parseMajorUnitsToSmallestUnit(input.amountInput, dispute.currency);
    if (amount !== undefined) {
      request.amount = amount;
    }
  }
  if (input.evidence.length > 0) {
    request.evidence = input.evidence;
  }
  return request;
};

/** The parsed accepted-liability amount in integer smallest units of `dispute.currency`, when valid. */
export const acceptedLiabilitySmallestUnit = (dispute: CardDispute, amountInput: string): number | undefined => {
  const amount = parseMajorUnitsToSmallestUnit(amountInput, dispute.currency);
  return amount !== undefined && amount > 0 ? amount : undefined;
};
